import { Box, Button, Chip, CircularProgress, Stack, Typography, Divider, Tooltip } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { preContractsApi } from 'api';

interface Props {
  employeeId: number | undefined;
  contractId: number | null | undefined;
}

interface PreContractRow {
  id: number;
  status: 'DRAFT' | 'SENT' | 'SIGNED' | 'EXPIRED';
  sentAt: string | null;
  signedAt: string | null;
  createdAt: string;
}

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

const PreContractActions: FC<Props> = ({ employeeId, contractId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadingSigned, setUploadingSigned] = useState(false);
  const [rows, setRows] = useState<PreContractRow[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchRows = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const res = await preContractsApi.findByEmployee(String(employeeId));
      setRows((res.data ?? []) as PreContractRow[]);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleDownloadPreview = async () => {
    if (!employeeId) return;
    setDownloading(true);
    try {
      const issued = await preContractsApi.issuePreContract({
        employeeId,
        contractId: contractId ?? undefined,
        sendEmail: false,
      } as any);
      const blob = await preContractsApi.downloadUnsignedPdf(
        String((issued.data as any).id),
        { responseType: 'blob' } as any,
      );
      const url = URL.createObjectURL(blob.data as unknown as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pre-contratto-${(issued.data as any).id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      await fetchRows();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('alerts.error'));
    } finally {
      setDownloading(false);
    }
  };

  const handleSend = async () => {
    if (!employeeId) return;
    setSending(true);
    try {
      await preContractsApi.issuePreContract({
        employeeId,
        contractId: contractId ?? undefined,
        sendEmail: true,
      } as any);
      toast.success(t('preContract.sent', 'Pre-contratto inviato al dipendente'));
      await fetchRows();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('alerts.error'));
    } finally {
      setSending(false);
    }
  };

  const handleUploadSigned = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !employeeId) return;
    setUploadingSigned(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Find the latest non-SIGNED row to attach the upload to. If none,
      // call the one-shot endpoint that creates a fresh SIGNED row.
      const latestOpen = rows.find((r) => r.status !== 'SIGNED');
      if (latestOpen) {
        await preContractsApi.uploadSigned(
          String(latestOpen.id),
          { data: fd } as any,
        );
      } else {
        if (contractId != null) {
          fd.append('contractId', String(contractId));
        }
        await preContractsApi.uploadSignedForEmployee(
          String(employeeId),
          { data: fd } as any,
        );
      }
      toast.success(t('preContract.uploadSuccess', 'Pre-contratto firmato caricato'));
      await fetchRows();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('alerts.error'));
    } finally {
      setUploadingSigned(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadSigned = async (id: number) => {
    try {
      const res = await preContractsApi.getSignedPdfUrl(String(id));
      window.open((res.data as any).url, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('alerts.error'));
    }
  };

  const latestSigned = rows.find((r) => r.status === 'SIGNED');
  const latestSent = rows.find((r) => r.status === 'SENT');

  return (
    <Box sx={{ mt: 3, p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50' }}>
      <Stack direction='row' alignItems='center' gap={1} mb={1.5}>
        <DescriptionIcon color='primary' />
        <Typography variant='subtitle1' fontWeight={600}>
          {t('preContract.title', 'Pre-contratto')}
        </Typography>
        {latestSigned ? (
          <Chip
            size='small'
            color='success'
            icon={<CheckCircleIcon />}
            label={`${t('preContract.signedOn', 'Firmato il')} ${formatDate(latestSigned.signedAt)}`}
            sx={{ ml: 1 }}
          />
        ) : latestSent ? (
          <Chip
            size='small'
            color='warning'
            icon={<HourglassEmptyIcon />}
            label={`${t('preContract.awaitingSignature', 'In attesa di firma')} · ${formatDate(latestSent.sentAt)}`}
            sx={{ ml: 1 }}
          />
        ) : null}
      </Stack>

      <Typography variant='body2' color='text.secondary' mb={2}>
        {t(
          'preContract.description',
          'Genera il pre-contratto precompilato con i dati di questo dipendente. Puoi scaricarlo direttamente o inviarlo via email per la firma elettronica.',
        )}
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
        <Tooltip title={t('preContract.downloadPreviewHint', 'Scarica il PDF precompilato senza inviare nulla')}>
          <span>
            <Button
              variant='outlined'
              startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={() => { void handleDownloadPreview(); }}
              disabled={downloading || !employeeId}
            >
              {t('preContract.downloadPreview', 'Scarica precompilato')}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={t('preContract.sendHint', 'Invia al dipendente con link di firma')}>
          <span>
            <Button
              variant='contained'
              startIcon={sending ? <CircularProgress size={16} color='inherit' /> : <SendIcon />}
              onClick={() => { void handleSend(); }}
              disabled={sending || !employeeId}
            >
              {t('preContract.send', 'Invia per firma')}
            </Button>
          </span>
        </Tooltip>
        {latestSigned && (
          <Button
            variant='contained'
            color='success'
            startIcon={<DownloadIcon />}
            onClick={() => { void handleDownloadSigned(latestSigned.id); }}
          >
            {t('preContract.downloadSigned', 'Scarica firmato')}
          </Button>
        )}
        <Tooltip
          title={t(
            'preContract.uploadSignedHint',
            'Hai già un PDF firmato (es. cartaceo scansionato)? Caricalo qui.',
          )}
        >
          <span>
            <Button
              variant='outlined'
              color='success'
              startIcon={uploadingSigned ? <CircularProgress size={16} /> : <UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingSigned || !employeeId}
            >
              {t('preContract.uploadSigned', 'Carica firmato (offline)')}
            </Button>
          </span>
        </Tooltip>
        <input
          ref={fileInputRef}
          type='file'
          accept='application/pdf'
          style={{ display: 'none' }}
          onChange={(e) => { void handleUploadSigned(e); }}
        />
      </Stack>

      {rows.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant='caption' color='text.secondary' display='block' mb={1}>
            {t('preContract.history', 'Storico')}
          </Typography>
          <Stack gap={0.5}>
            {rows.slice(0, 5).map((r) => (
              <Stack key={r.id} direction='row' gap={1} alignItems='center' sx={{ fontSize: '0.8rem' }}>
                <Chip
                  size='small'
                  label={r.status}
                  color={r.status === 'SIGNED' ? 'success' : r.status === 'SENT' ? 'warning' : 'default'}
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
                <Typography variant='caption' color='text.secondary'>
                  {formatDate(r.signedAt ?? r.sentAt ?? r.createdAt)}
                </Typography>
                {r.status === 'SIGNED' && (
                  <Button
                    size='small'
                    onClick={() => { void handleDownloadSigned(r.id); }}
                    sx={{ py: 0, minWidth: 0 }}
                  >
                    {t('common.download', 'scarica')}
                  </Button>
                )}
              </Stack>
            ))}
          </Stack>
        </>
      )}
      {loading && <CircularProgress size={16} sx={{ mt: 1 }} />}
    </Box>
  );
};

export default PreContractActions;
